import { AgentStepPreview } from 'configuration-schemas/viewdefns/viewdefn';
import React, { useState, useEffect } from 'react';
import { BasicItem } from 'src/worker/pivotWorker.types';
import { AppType } from 'configuration-schemas/general';
import ServiceContainer from 'src/ServiceContainer';
import Axios from 'src/services/axios';
import { isEmpty, groupBy } from 'lodash';
import { toast } from 'react-toastify';
import { isListDataApi } from 'configuration-schemas/confdefnView';
import { AgentChat } from '../AgentChat/AgentChat';
import { AgentChatMessageType } from '../Agent.types';
import { processApiParams } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import Renderer, { RendererOptions } from 'src/utils/Domain/Renderer';
import { Divider, Paper } from '@material-ui/core';

export interface PillProps {
  text: string;
  valueText: string;
}

export const PillRenderer = ({ text, valueText }: PillProps) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        background: '#f5f1fb',
        borderRadius: '25px',
        fontSize: '0.75rem',
        padding: '7px',
      }}
    >
      <span className="pill-header" style={{ fontWeight: 'bold' }}>
        {text}
      </span>
      <Divider orientation="vertical" flexItem={true} style={{ margin: '0 7px' }} />
      <span className="pill-value">{valueText}</span>
    </div>
  );
};

interface AgentPreviewProps {
  step: AgentStepPreview;
  scope: {
    productMember: string | null;
    locationMember: string | null;
  };
  onDataLoaded: () => void;
}

export const AgentPreview = ({ step, scope, onDataLoaded }: AgentPreviewProps) => {
  const [data, setData] = useState<BasicItem[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const getData = async () => {
    // TODO add check for if data is already loading
    const { dataApi } = step;

    if (!dataApi) {
      return;
    }

    setIsLoading(true);

    try {
      if (isListDataApi(dataApi)) {
        const modifiedScope = {
          ...scope,
          appName: 'Assortment',
          floorsets: dataApi.params.floorsets,
          aggBy: dataApi.params.aggBy || 'level:class',
        };

        const processedApi = processApiParams(dataApi, modifiedScope);

        const params = {
          ...dataApi.params,
          ...processedApi.params,
        };

        const resp = await ServiceContainer.pivotService.listData(processedApi.defnId, AppType.Assortment, params);
        const flatData = resp.flat;

        setData(!isEmpty(flatData) ? flatData : undefined);
        onDataLoaded();
      } else {
        const resp = await Axios.get(dataApi.url, {
          params: dataApi.params,
        });
        let responseData = resp.data.data;

        if (responseData?.data) {
          responseData = responseData.data;
        }

        if (Array.isArray(responseData)) {
          setData(!isEmpty(responseData) ? responseData : undefined);
          onDataLoaded();
        } else {
          setData(responseData ? [responseData] : undefined);
          onDataLoaded();
        }
      }
    } catch (err) {
      toast.error('An error occurred fetching the preview data');
      ServiceContainer.loggingService.error(`An error occurred: ${(err as Error).message}`, (err as Error).stack);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const renderGroupedData = () => {
    if (!data || !step.dataApi || !isListDataApi(step.dataApi)) {
      return null;
    }

    const aggBy = step.dataApi.params.aggBy || 'level:class';
    const groupedData = groupBy(data, aggBy);

    return (
      <div>
        {Object.entries(groupedData).map(([groupKey, groupItems]) => (
          <div key={groupKey} style={{ marginBottom: '20px' }}>
            {groupItems.map((item, itemIndex) => {
              if (step.view.length <= 0) return <></>;

              const firstViewItem = step.view[0];
              const remainingViewItems = step.view.slice(1);

              const firstValue = item[firstViewItem.dataIndex];
              const firstRendererFn = Renderer[firstViewItem.renderer as RendererOptions];
              // @ts-expect-error
              const firstRenderedValue = firstRendererFn ? firstRendererFn(firstValue, firstViewItem.mask) : firstValue;

              return (
                <Paper
                  key={itemIndex}
                  style={{ display: 'flex', marginLeft: '10px', marginBottom: '10px', padding: '12px' }}
                >
                  <div
                    style={{
                      fontWeight: 'bold',
                      marginBottom: '5px',
                      flexGrow: 1,
                      textAlign: 'center',
                      minWidth: '25%',
                      maxWidth: '25%',
                      width: '25%',
                    }}
                  >
                    {firstRenderedValue}
                  </div>
                  <Divider orientation="vertical" flexItem={true} />
                  <div
                    style={{
                      display: 'flex',
                      gap: '1rem',
                      flexWrap: 'wrap',
                      flexGrow: 2,
                      justifyContent: 'space-evenly',
                    }}
                  >
                    {remainingViewItems.map((viewItem, viewIndex) => {
                      const value = item[viewItem.dataIndex];
                      const rendererFn = Renderer[viewItem.renderer as RendererOptions];
                      // @ts-expect-error
                      const renderedValue = rendererFn ? rendererFn(value, viewItem.mask) : value;
                      return <PillRenderer key={viewIndex} text={viewItem.text ?? ''} valueText={renderedValue} />;
                    })}
                  </div>
                </Paper>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // TODO add animated loading spinner
  return (
    <div>
      <AgentChat type={AgentChatMessageType.agent} message={step.text} />
      {isLoading && <div>Loading...</div>}
      {!isLoading && !data && <div>No data available</div>}
      {!isLoading && data && renderGroupedData()}
    </div>
  );
};
